import React, { useState } from 'react'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { MessagesSquare, Settings, Import, FilePlus, Sparkles, Package, DollarSign, User2, Building2 } from 'lucide-react'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import DirhamsIcon from '@/components/ui/dirhams-icon'
import { tableHeadClass, tableCellClass} from '@/components/ui/table-styles'
import PaginationButton from '@/components/ui/pagination-button'
import {Route as purchaseOrderRoute} from '@/routes/(inventory)/branch/_layout/purchase-orders/index'
import { useRouter } from '@tanstack/react-router'
import { convertToCurrencyRounded } from '@/utils/currency_converter'
import SimpleHeader from '@/components/simple-header'

const departmentColors = [
  '#FF6B6B', // red
  '#6BCB77', // green
  '#4D96FF', // blue
  '#FFD93D', // yellow
  '#A66CFF', // purple
]

const Screen = () => {
  const data = purchaseOrderRoute.useLoaderData()
  const params = purchaseOrderRoute.useSearch()
  const router = useRouter();

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRowExpansion = (rowKey: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowKey)) {
      newExpanded.delete(rowKey);
    } else {
      newExpanded.add(rowKey);
    }
    setExpandedRows(newExpanded);
  };

  return (
    <>
      <SimpleHeader text="Purchase Orders" />
      <div className="flex items-center justify-between">
        {/* Department Select */}
        <Select value={params.stockPointId ? params.stockPointId : ''} onValueChange={(e) => {
            router.navigate({
              search: {
                ...params,
                stockPointId: e,
              }
            })
        }}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Select department...">
              {(() => {
                const selected = data.stockPoints.stockPoints.find(opt => opt.id === params.stockPointId) || { id: 'all', name: 'All Stock Points' }
                const idx = data.stockPoints.stockPoints.findIndex(opt => opt.id === selected.id)
                const color = idx > 0 ? departmentColors[(idx - 1) % departmentColors.length] : undefined
                return selected ? (
                  <span className="flex items-center gap-2">
                    {selected.id !== 'all' && <span style={{ background: color, borderRadius: '4px', width: 16, height: 16, display: 'inline-block' }} />}
                    {selected.name}
                  </span>
                ) : null
              })()}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                All Stock Points
              </span>
            </SelectItem>
            {data.stockPoints.stockPoints.map((option, idx) => (
              <SelectItem key={option.id} value={option.id}>
                <span className="flex items-center gap-2">
                  {option.id !== 'all' && <span style={{ background: departmentColors[(idx - 1) % departmentColors.length], borderRadius: '4px', width: 16, height: 16, display: 'inline-block' }} />}
                  {option.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Right side buttons */}
        <div className="flex gap-2 items-center">
          <Button size="sm" variant="outline" aria-label="Chat">
            <MessagesSquare className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" aria-label="Create Purchase Order" onClick={() => { router.navigate({ to: "/branch/purchase-orders/create" }) }}>
            <FilePlus className="w-4 h-4 mr-1" />
            Create Purchase Order
          </Button>
          <Button size="sm" variant="outline" aria-label="View Settings">
            <Settings className="w-4 h-4 mr-1" />
            View Settings
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button size="sm" variant="outline" aria-label="Import/Export">
                <Import className="w-4 h-4 mr-1" />
                Import/Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Import</DropdownMenuItem>
              <DropdownMenuItem>Export</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {/* Table Section */}
      <div className="mt-4 border rounded-lg bg-white overflow-hidden">
        <Table className="text-sm">
          <TableHeader>
            <TableRow className="text-sm">
              <TableHead className={tableHeadClass}>Purchase Order</TableHead>
              <TableHead className={tableHeadClass}>Products</TableHead>
              <TableHead className={tableHeadClass}>Approx Cost</TableHead>
              <TableHead className={tableHeadClass}>Supplier Name</TableHead>
              <TableHead className={tableHeadClass}>Stock Point Name</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
          {data.purchaseOrders.purchaseOrders.map((po, i) => {
            const rowKey = `po-${po.id}`;
            const isExpanded = expandedRows.has(rowKey);
            const isLastRow = i === data.purchaseOrders.purchaseOrders.length - 1;
            
            return (
              <React.Fragment key={po.id}>
                <TableRow className="font-medium">
                  <TableCell 
                    className={tableCellClass}
                    onClick={() => {
                      router.navigate({
                        to: "/branch/purchase-orders/$id",
                        params: { id: po.id },
                        search: {
                          stockPointId: params.stockPointId || 'all',
                          supplierId: params.supplierId || 'all',
                          index: i,
                          total: data.purchaseOrders.totalCount,
                        }
                      })
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {po.orderNumber}
                  </TableCell>
                  <TableCell 
                    className={tableCellClass}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRowExpansion(rowKey);
                    }}
                  >
                    <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors">
                      <svg 
                        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span>{po.items.length}</span>
                    </div>
                  </TableCell>
                  <TableCell 
                    className={tableCellClass}
                    onClick={() => {
                      router.navigate({
                        to: "/branch/purchase-orders/$id",
                        params: { id: po.id },
                        search: {
                          stockPointId: params.stockPointId || 'all',
                          supplierId: params.supplierId || 'all',
                          index: i,
                          total: data.purchaseOrders.totalCount,
                        }
                      })
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {convertToCurrencyRounded(
                      po.items.reduce((total, item) => total + (item.quantity * item.unitPrice), 0)
                    )}
                  </TableCell>
                  <TableCell 
                    className={tableCellClass}
                    onClick={() => {
                      router.navigate({
                        to: "/branch/purchase-orders/$id",
                        params: { id: po.id },
                        search: {
                          stockPointId: params.stockPointId || 'all',
                          supplierId: params.supplierId || 'all',
                          index: i,
                          total: data.purchaseOrders.totalCount,
                        }
                      })
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {po.supplierName}
                  </TableCell>
                  <TableCell 
                    className={tableCellClass}
                    onClick={() => {
                      router.navigate({
                        to: "/branch/purchase-orders/$id",
                        params: { id: po.id },
                        search: {
                          stockPointId: params.stockPointId || 'all',
                          supplierId: params.supplierId || 'all',
                          index: i,
                          total: data.purchaseOrders.totalCount,
                        }
                      })
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {po.stockPointName}
                  </TableCell>
                </TableRow>
                
                {isExpanded && (
                  <TableRow className="bg-gray-50">
                    <TableCell 
                      colSpan={5}
                    >
                      <div className="py-2 px-4">
                        <div className="text-sm font-semibold text-gray-700 mb-3">Items in Order</div>
                        <div className="grid gap-2">
                          {po.items && po.items.length > 0 ? (
                            po.items.map((item, itemIdx) => (
                              <div 
                                key={itemIdx}
                                className="flex items-center justify-between py-2 px-3 bg-white rounded-md border"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 font-semibold text-xs">
                                    {itemIdx + 1}
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium">{item.tradeName}</span>
                                    <span className="text-xs text-gray-500 ml-2">
                                      ({item.unit} {item.unitType})
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className="text-sm text-gray-600">
                                    Qty: <span className="font-semibold">{Math.floor(item.quantity)}</span>
                                  </span>
                                  {item.bonusQuantity > 0 && (
                                    <span className="text-sm text-green-600">
                                      Bonus: <span className="font-semibold">{Math.floor(item.bonusQuantity)}</span>
                                    </span>
                                  )}
                                  <span className="text-sm text-gray-600">
                                    Price: <span className="font-semibold">{convertToCurrencyRounded(item.unitPrice)}</span>
                                  </span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4 text-sm text-gray-500">
                              No items found
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
          <p>{data.purchaseOrders.totalCount} <span className="text-gray-500 font-light">count</span></p>
          <div className="flex items-center gap-1">
            <PaginationButton onClick={() => {
              router.navigate({
                search: {
                  ...params,
                  page: 1,
                }
              })
            }} disabled={
              params.page <= 1
            }>
              <span className="sr-only">First</span>
              {/* Use a left double arrow icon if available */}
              &laquo;
            </PaginationButton>
            <PaginationButton onClick={() => {
              router.navigate({
                search: {
                  ...params,
                  page: params.page - 1,
                }
              })
            }} disabled={
              params.page <= 1
            }>
              <span className="sr-only">Previous</span>
              &lsaquo;
            </PaginationButton>
            {Array.from({ length: Math.ceil(data.purchaseOrders.totalCount/params.pageSize) }, (_, idx) => (
              <PaginationButton
                key={idx + 1}
                onClick={() => {
                  router.navigate({
                    search: {
                      ...params,
                      page: idx + 1,
                    }
                  })
                }}
                active={params.page === idx + 1}
              >
                <span className="text-sm">{idx + 1}</span>
              </PaginationButton>
            ))}
            <PaginationButton onClick={() => {
              router.navigate({
                search: {
                  ...params,
                  page: params.page + 1,
                }
              })
            }} disabled={
              params.page >= Math.ceil(data.purchaseOrders.totalCount / params.pageSize)
            }>
              <span className="sr-only">Next</span>
              &rsaquo;
            </PaginationButton>
            <PaginationButton onClick={() => {
              router.navigate({
                search: {
                  ...params,
                  page: Math.ceil(data.purchaseOrders.totalCount / params.pageSize),
                }
              })
            }} disabled={
              params.page >= Math.ceil(data.purchaseOrders.totalCount / params.pageSize)
            }>
              <span className="sr-only">Last</span>
              &raquo;
            </PaginationButton>
          </div>
        </div>
      </div>
    </>
  )
}

export default Screen